import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createApiClient } from './client.js';
import { clearStoredAuth, getStoredEmail, getStoredToken, setStoredEmail, setStoredToken } from './tokenStore.js';

// Magic-link auth for this app, against this app's own API.
//
// It does one job: hold a session, prove it, drop it. There is no tenant switching and
// no user object, because this API's whole identity model is "an email on the
// allowlist" — carrying a richer shape than the server returns is how you get fields
// that are permanently null and a reader who assumes they mean something.
//
// The flow: email → the API mails a link → the link lands on /verify?token=… →
// POST /api/auth/verify returns { token, email } → into sessionStorage.

const Ctx = createContext(null);

export function AuthProvider({ children, baseUrl }) {
  const client = useMemo(() => createApiClient({ baseUrl }), [baseUrl]);
  const [token, setToken] = useState(() => getStoredToken());
  const [email, setEmail] = useState(() => getStoredEmail());

  const logout = useCallback(() => {
    clearStoredAuth();
    setToken(null); setEmail(null);
  }, []);

  // A 401 from ANY request ends the session. Centralised so no caller has to remember
  // to handle it, and so a stale token cannot leave the UI half-logged-in.
  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [logout]);

  const sendMagicLink = useCallback((email) => client.auth.sendMagicLink(email), [client]);

  const verifyMagicLink = useCallback(async (linkToken) => {
    const r = await client.auth.verifyMagicLink(linkToken);
    const d = r?.data || r || {};
    if (!d.token) throw new Error('That link did not return a session. Ask for a new one.');
    setStoredToken(d.token); setToken(d.token);
    if (d.email) { setStoredEmail(d.email); setEmail(d.email); }
    return d;
  }, [client]);

  const value = useMemo(() => ({
    api: client.api,
    token, email,
    isAuthenticated: !!token,
    sendMagicLink, verifyMagicLink, logout
  }), [client, token, email, sendMagicLink, verifyMagicLink, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within an AuthProvider');
  return v;
}
